import React, { useContext, useEffect, useState } from 'react';
import CompanyLayout from '../../components/Company/CompanyLayout';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import ApplicationsChart from '../../components/Company/Charts/ApplicationsChart';
import CandidatesPieChart from '../../components/Company/Charts/CandidatesPieChart';
import SkillsBarChart from '../../components/Company/Charts/SkillsBarChart';
import * as companyService from '../../services/companyService';
import * as companyJobService from '../../services/companyJobService';
import * as companyResumeService from '../../services/companyResumeService';
import { CompanyContext } from '../../context/CompanyContext';

export default function CompanyDashboard() {
	const { company } = useContext(CompanyContext);
	const [stats, setStats] = useState({ totalJobs: 0, activeJobs: 0, totalApplications: 0, topMatch: { name: '', score: 0 } });
	const [recentJobs, setRecentJobs] = useState([]);
	const [topCandidates, setTopCandidates] = useState([]);

	useEffect(() => {
		(async () => {
			try {
				const s = await companyService.getCompanyStats();
				setStats(s);
				const jobs = await companyJobService.getCompanyJobs({ page: 1, limit: 5 });
				setRecentJobs(jobs.jobs || []);
				const resumes = await companyResumeService.getCompanyResumes({ page: 1, limit: 5 });
				setTopCandidates((resumes.resumes || []).map((r) => ({ name: r.sections?.personalInfo?.name || 'Candidate', score: r.atsScore || 0, skills: (r.sections?.skills || []).flatMap((c) => c.items || []).slice(0, 5) })));
			} catch {
			}
		})();
	}, []);

	return (
		<CompanyLayout>
			<div className="space-y-6">
				<header>
					<h1 className="text-2xl font-bold">Welcome, {company?.companyName || 'Company'}</h1>
				</header>

				{/* Stats */}
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
					<Stat title="Total Job Postings" value={stats.totalJobs} action="View All" />
					<Stat title="Active Jobs" value={stats.activeJobs} action={`${Math.round((stats.activeJobs / (stats.totalJobs || 1)) * 100)}% active`} />
					<Stat title="Total Applications" value={stats.totalApplications} action="View Resumes" />
					<Stat title="Top Candidate Match" value={`${stats.topMatch?.score || 0}%`} action={stats.topMatch?.name || ''} />
				</div>

				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
					<Card title="Recent Job Postings" className="lg:col-span-2">
						<div className="overflow-x-auto">
							<table className="min-w-full text-sm">
								<thead className="bg-gray-50">
									<tr>
										<th className="text-left px-4 py-2">Title</th>
										<th className="text-left px-4 py-2">Posted</th>
										<th className="text-left px-4 py-2">Applications</th>
										<th className="text-left px-4 py-2">Status</th>
										<th className="text-left px-4 py-2">Actions</th>
									</tr>
								</thead>
								<tbody>
									{!recentJobs.length ? (
										<tr>
											<td colSpan="5" className="px-4 py-6 text-center text-gray-500">No recent jobs.</td>
										</tr>
									) : (
										recentJobs.map((j) => (
											<tr key={j._id} className="border-t">
												<td className="px-4 py-2">{j.title}</td>
												<td className="px-4 py-2">{new Date(j.postedDate).toLocaleDateString()}</td>
												<td className="px-4 py-2">{j.applicationsCount || 0}</td>
												<td className="px-4 py-2">{j.isActive ? 'Active' : 'Closed'}</td>
												<td className="px-4 py-2">
													<Button variant="ghost">View</Button>
													<Button variant="ghost">Edit</Button>
													<Button variant="ghost">Analytics</Button>
												</td>
											</tr>
										))
									)}
								</tbody>
							</table>
						</div>
						<div className="mt-3">
							<Button variant="ghost">View All Jobs</Button>
						</div>
					</Card>
					<Card title="Top Candidates">
						<div className="space-y-2">
							{!topCandidates.length ? (
								<p className="text-sm text-gray-500">No candidates yet.</p>
							) : (
								topCandidates.map((c, i) => (
									<div key={i} className="border rounded p-2">
										<p className="font-semibold">{c.name}</p>
										<p className="text-sm text-gray-600">Match: {c.score}%</p>
										<p className="text-xs text-gray-500 truncate">Skills: {c.skills.join(', ')}</p>
										<div className="mt-2">
											<Button size="sm" variant="ghost">View Profile</Button>
										</div>
									</div>
								))
							)}
						</div>
					</Card>
				</div>

				{/* Analytics */}
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
					<Card title="Applications Over Time">
						<ApplicationsChart data={[{ date: 'Jan', applications: 10 }, { date: 'Feb', applications: 14 }, { date: 'Mar', applications: 20 }]} />
					</Card>
					<Card title="Candidates by Experience">
						<CandidatesPieChart data={[{ name: 'Entry', value: 10 }, { name: 'Mid', value: 20 }, { name: 'Senior', value: 5 }]} />
					</Card>
					<Card title="Top Skills">
						<SkillsBarChart data={[{ skill: 'React', count: 12 }, { skill: 'Node', count: 9 }, { skill: 'SQL', count: 7 }]} />
					</Card>
				</div>

				{/* Quick Actions */}
				<div className="space-y-3">
					<h2 className="text-lg font-semibold">Quick Actions</h2>
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
						<Button>Post New Job</Button>
						<Button>Upload Resumes</Button>
						<Button>Search Candidates</Button>
						<Button>View Analytics</Button>
					</div>
				</div>
			</div>
		</CompanyLayout>
	);
}

function Stat({ title, value, action }) {
	return (
		<Card>
			<p className="text-sm text-gray-500">{title}</p>
			<p className="mt-1 text-3xl font-bold">{value}</p>
			{action && <p className="text-xs text-gray-500 mt-1">{action}</p>}
		</Card>
	);
}


